import type {PaginationState, SortingState, Updater} from "@tanstack/react-table";
import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {getStoredServerTablePageSize} from "./pagination/utils";
import type {ServerTablePresentation, ServerTableUrlStateOptions} from "./types";
import {parseServerTableUrlState, writeServerTableUrlState} from "./urlState";
import type {ServerTableUrlQueryState} from "./urlState";

interface UseServerTableQueryStateOptions {
    defaultPageSize: number
    initialSorting: SortingState
    urlState?: ServerTableUrlStateOptions
    presentation?: ServerTablePresentation
}

interface LocalServerTableQueryState extends ServerTableUrlQueryState {
    pagination: PaginationState
    sorting: SortingState
}

// Changing what the data means (search, filters, sorting) always restarts at page one.
const firstPage = (pagination: PaginationState): PaginationState => (
    pagination.pageIndex === 0 ? pagination : {...pagination, pageIndex: 0}
);

/**
 * Owns the table's data-affecting query state: pagination, search, filters, and sorting.
 *
 * Pagination and sorting remain local view state. Without `urlState`, search and filters
 * are local too. With `urlState`, only those durable selection criteria are parsed from
 * and written to the URL so shared links remain useful as the underlying data changes.
 */
export const useServerTableQueryState = ({
    defaultPageSize,
    initialSorting,
    urlState,
    presentation = 'standard'
}: UseServerTableQueryStateOptions) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // The configuration is frozen on mount so the URL keeps parsing the same way for the
    // lifetime of this table instance, regardless of caller-side option identity churn.
    const [config] = useState(() => ({
        urlState,
        // An embedded table is sized by the surface around it, so its page size is the
        // caller's number rather than whatever page size the user last picked elsewhere.
        initialPageSize: presentation === 'embedded'
            ? defaultPageSize
            : getStoredServerTablePageSize(defaultPageSize),
        initialSorting: initialSorting.map(sort => ({...sort}))
    }));
    const [localState, setLocalState] = useState<LocalServerTableQueryState>(() => ({
        pagination: {pageIndex: 0, pageSize: config.initialPageSize},
        searchQuery: '',
        filterRules: [],
        sorting: config.initialSorting
    }));

    const searchParamsValue = searchParams.toString();
    const parsedUrlState = useMemo(() => config.urlState
        ? parseServerTableUrlState(new URLSearchParams(searchParamsValue), {
            urlState: config.urlState
        })
        : undefined, [config, searchParamsValue]);
    const searchQuery = parsedUrlState?.searchQuery ?? localState.searchQuery;
    const filterRules = parsedUrlState?.filterRules ?? localState.filterRules;
    const sharedCriteriaIdentity = config.urlState
        ? JSON.stringify([searchQuery, filterRules])
        : undefined;
    const previousSharedCriteriaIdentityRef = useRef(sharedCriteriaIdentity);
    const sharedCriteriaChanged = previousSharedCriteriaIdentityRef.current !== sharedCriteriaIdentity;
    const pagination = sharedCriteriaChanged
        ? firstPage(localState.pagination)
        : localState.pagination;

    // Browser navigation can restore different shared criteria without using our setters.
    // Reset the local page in the same render that exposes those restored criteria.
    useLayoutEffect(() => {
        if (previousSharedCriteriaIdentityRef.current === sharedCriteriaIdentity) return;

        previousSharedCriteriaIdentityRef.current = sharedCriteriaIdentity;
        setLocalState(current => ({
            ...current,
            pagination: firstPage(current.pagination)
        }));
    }, [sharedCriteriaIdentity]);

    const commitUrlState = useCallback((next: ServerTableUrlQueryState, replace = false) => {
        const urlStateConfig = config.urlState;
        if (!urlStateConfig) return;

        const params = new URLSearchParams(searchParamsValue);
        writeServerTableUrlState(params, next, {urlState: urlStateConfig});
        setSearchParams(params, {replace, preventScrollReset: true});
    }, [config, searchParamsValue, setSearchParams]);

    const setPagination = useCallback((updater: Updater<PaginationState>) => {
        setLocalState(current => ({
            ...current,
            pagination: typeof updater === 'function' ? updater(current.pagination) : updater
        }));
    }, []);

    const replacePageIndex = useCallback((pageIndex: number) => {
        setLocalState(current => ({
            ...current,
            pagination: {...current.pagination, pageIndex}
        }));
    }, []);

    const setSearchQuery = useCallback((searchQuery: string) => {
        setLocalState(current => ({
            ...current,
            searchQuery: config.urlState ? current.searchQuery : searchQuery,
            pagination: firstPage(current.pagination)
        }));

        // Replaced, not pushed: every debounced keystroke as a history entry would make
        // the back button useless.
        commitUrlState({searchQuery, filterRules}, true);
    }, [commitUrlState, config.urlState, filterRules]);

    const setFilterRules = useCallback((filterRules: FilterRule[]) => {
        setLocalState(current => ({
            ...current,
            filterRules: config.urlState ? current.filterRules : filterRules,
            pagination: firstPage(current.pagination)
        }));
        commitUrlState({searchQuery, filterRules});
    }, [commitUrlState, config.urlState, searchQuery]);

    const setSorting = useCallback((updater: Updater<SortingState>) => {
        setLocalState(current => ({
            ...current,
            sorting: typeof updater === 'function' ? updater(current.sorting) : updater,
            pagination: firstPage(current.pagination)
        }));
    }, []);

    return {
        pagination,
        setPagination,
        replacePageIndex,
        searchQuery,
        setSearchQuery,
        filterRules,
        setFilterRules,
        sorting: localState.sorting,
        setSorting
    };
};
